import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';

const PreviewStatement = () => {
  const [searchParams] = useSearchParams();
  const { previewId: urlPreviewId } = useParams();

  // Get previewId from either URL param or query param
  const previewId = urlPreviewId || searchParams.get('previewId');

  const [statements, setStatements] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [previousRunDate, setPreviousRunDate] = useState(null);
  const [runDateState, setRunDateState] = useState(null);
  const [displayDate, setDisplayDate] = useState('');
  const [labInfo, setLabInfo] = useState(null);
  const [filters, setFilters] = useState({});

  // Helper functions
  const formatIfNonZero = (amount) => {
    const safeAmount = amount || 0;
    return safeAmount !== 0 ?
    (Math.abs(safeAmount)) : '';
  };

  const formatCredit = (amount) => {
    const safeAmount = amount || 0;
    return safeAmount !== 0 ? `-${
        (Math.abs(safeAmount))}` : '';
  };

  const formatDebit = (amount) => {
    if (!amount || Number(amount) === 0) return "";
    const num = Number(amount);
    return num < 0 ? `-${
        (Math.abs(num))}` :
        (num);
  };

  const removeLeadingZeros = (str) => {
    if (!str) return '';
    const result = String(str).replace(/^0+/, '');
    return result === '' ? '0' : result;
  };

  const getPatientName = (invoice) => {
    if (invoice.patientName) return invoice.patientName;
    if (invoice.patientFirstName || invoice.patientLastName) {
      return `${invoice.patientFirstName || ''} ${invoice.patientLastName || ''}`.trim();
    }
    return '';
  };

  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return 'Invalid Date';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return 'Invalid Date';
    const day = d.getDate().toString().padStart(2, '0');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${day} ${monthNames[d.getMonth()]} ${d.getFullYear()}`;
  };

  const isDateAfter = (dateStr, compareDateStr) => {
    if (!dateStr || !compareDateStr) return true;
    const date = new Date(dateStr);
    const compareDate = new Date(compareDateStr);
    if (isNaN(date.getTime()) || isNaN(compareDate.getTime())) return true;
    return date > compareDate;
  };

  const renderNote = (note) => {
    if (!note) return null;
    return note.split('\n').map((line, i) => (
      <div key={i}>{line}<br /></div>
    ));
  };

  // Fetch lab info
  useEffect(() => {
    const fetchLabInfo = async () => {
      try {
        const response = await axios.get('https://uk.backend.hms.hiradentallab.com/api/auth/users', {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        if (response.data.success && response.data.users.length > 0) {
          setLabInfo(response.data.users[0]);
        }
      } catch (err) {
        console.error('Lab info fetch error:', err);
      }
    };
    fetchLabInfo();
  }, []);

  // Fetch preview statements
  useEffect(() => {
    const fetchPreviewStatements = async () => {
      if (!previewId) {
        setError('No preview ID provided. Please make sure you are accessing this page with a valid preview ID.');
        setIsLoading(false);
        return;
      }
      try {
        setIsLoading(true);
        setError(null);

        const token = localStorage.getItem('token');

        const response = await axios.get(`https://uk.backend.hms.hiradentallab.com/api/statements/preview/${previewId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.data.success && response.data.data) {
          const previewData = response.data.data;
          setStatements(previewData.statements || []);
          setPreviousRunDate(previewData.lastRunTime || previewData.runDate);
          setRunDateState(previewData.runDate);
          setDisplayDate(previewData.displayDate);
          setFilters(previewData.filters || {});
        } else {
          setError(response.data.message || 'No preview data available');
          setStatements([]);
        }
      } catch (err) {
        console.error('Fetch preview error:', err);
        setError(err.response?.data?.message || 'Failed to load statement preview');
        setStatements([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchPreviewStatements();
  }, [previewId]);

  const handlePrint = () => window.print();

  if (error) {
    return (
      <div className="error-message" style={{ padding: '20px', color: 'red' }}>
        <h3>Error</h3>
        <p>{error}</p>
        <br />
        <button
          onClick={() => window.close()}
          style={{
            marginTop: '20px',
            padding: '10px 20px',
            backgroundColor: '#004aad',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Close Window
        </button>
      </div>
    );
  }

  return (
    <div className="statement-container">
      {isLoading ? (
        <p>Loading statement preview...</p>
      ) : statements.length === 0 ? (
        <p>No statement preview available.</p>
      ) : (
        <>
          <div className="header-controls">
            <button className="print-button" onClick={handlePrint}>
              Print
            </button>
          </div>
          {statements.map((statement, idx) => {
            // Filter out zero balance statements if dontPrintZeroBalance is true
            if (filters.dontPrintZeroBalance &&
                Number(statement.newBalance || 0) === 0 &&
                Number(statement.previousBalance || 0) === 0) {
              return null;
            }

            // Get provider groups
            const providerGroups = statement.groupedByProvider || {};
            const hasProviderGroups = Object.keys(providerGroups).length > 0;

            return (
              <div key={`${statement.accountId}-${idx}`} className="statement-wrapper"
                   style={{ marginBottom: '40px', pageBreakBefore: idx > 0 ? 'always' : 'auto' }}>

                {/* Header */}
                <div className="header-section">
                  <div className="lab-info">
                    <strong>{labInfo?.labName || 'Hira Dental Laboratory'}</strong><br />
                    {labInfo?.address || '02 Ulverscroft Road,'}<br />
                    {labInfo?.city || 'Coventry'}, {labInfo?.state || ''} {labInfo?.zipCode || 'COV CV3 5EZ'}
                  </div>

                  <div className="header-right">
                    <div className="statement-info">
                      <div className="statement-date">
                        <strong>Statement for {formatDisplayDate(statement.displayDate || displayDate || runDateState)}</strong>
                      </div>
                      <div className="contact-info">
                        <span className="telephone-nowrap">
                          {labInfo?.telephone || '02476263962'}
                        </span><br />
                        {labInfo?.labEmail || 'info@hiradentallab.com'}
                        {previousRunDate && (
                          <div className="previous-statement">
                            Previous: {formatDisplayDate(previousRunDate)}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="logo-container-right">
                      <img src="/log.png" alt="Logo" className="header-logo" />
                    </div>
                  </div>
                </div>

                <h3 className="statement-title">Statement for {formatDisplayDate(statement.displayDate || displayDate || runDateState)}</h3>

                <div className="client-payment-section">
                  <div className="client-info">
                    <strong>{statement.accountHolder}</strong><br />
                    {statement.address || 'Address not available'}<br />
                    {[statement.city, statement.state, statement.zip].filter(Boolean).join(', ')}
                  </div>
                  <div className="payment-details">
                    {statement.note ? renderNote(statement.note) : (
                      <>
                        Telephone: {statement.telephone || 'Not available'}<br />
                        Email: {statement.email || 'Not available'}
                      </>
                    )}
                  </div>
                </div>

                <div className="table-wrapper">
                  <table className="statement-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Description</th>
                        <th>Debit</th>
                        <th>Credit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {hasProviderGroups ? (
                        Object.values(providerGroups)
                          .sort((a, b) => (a.providerName || '').localeCompare(b.providerName || ''))
                          .map((group) => {
                            const lines = [];

                            // Calculate provider totals
                            let providerTotalDebit = 0;
                            let providerTotalCredit = 0;

                            // Add up all invoice debits
                            (group.invoices || []).forEach(invoice => {
                              const debit = Number(invoice.debit || invoice.total || 0);
                              providerTotalDebit += debit;
                            });

                            // Add up all transaction credits
                            (group.transactions || []).forEach(trans => {
                              providerTotalCredit += Number(trans.amount || 0);
                            });

                            // Calculate provider subtotal
                            const providerSubtotal = providerTotalDebit - providerTotalCredit;

                            // Previous Balance line (only if exists)
                            if (group.previousBalance > 0) {
                              const prevDate = statement.lastRunDate ? new Date(statement.lastRunDate) : new Date();
                              lines.push({
                                sortDate: prevDate,
                                date: formatDisplayDate(statement.lastRunDate),
                                desc: 'Previous Balance',
                                debit: formatDebit(group.previousBalance),
                                credit: '',
                                type: 'previousBalance'
                              });
                            }

                            // Get invoices that are AFTER the previous run date
                            const currentInvoices = (group.invoices || []).filter(invoice => {
                              const invoiceDate = invoice.deliveryDate || invoice.invoiceDate;
                              return isDateAfter(invoiceDate, statement.lastRunDate);
                            });

                            // Add current period invoices
                            currentInvoices.forEach((invoice) => {
                              const invoiceDate = invoice.deliveryDate || invoice.invoiceDate;
                              const formattedInvoiceNumber = removeLeadingZeros(invoice.invoiceNumber);
                              const debit = invoice.debit || invoice.total || 0;
                              const invDate = new Date(invoiceDate);

                              lines.push({
                                sortDate: invDate,
                                date: formatDisplayDate(invoiceDate),
                                desc: `Case Invoice ${formattedInvoiceNumber} / ${getPatientName(invoice)}`,
                                debit: formatDebit(debit),
                                credit: '',
                                type: 'invoice'
                              });
                            });

                            // Add transactions (payments/credits)
                            (group.transactions || []).forEach((trans) => {
                              const transDate = new Date(trans.date);
                              lines.push({
                                sortDate: transDate,
                                date: formatDisplayDate(trans.date),
                                desc: trans.displayDescription || trans.description,
                                debit: '',
                                credit: formatCredit(trans.amount),
                                type: 'transaction'
                              });
                            });

                            // Sort all lines by date
                            lines.sort((a, b) => a.sortDate - b.sortDate);

                            return (
                              <React.Fragment key={group.providerId}>
                                <tr className="provider-row">
                                  <td colSpan="4">
                                    <strong>{group.providerName}</strong>
                                  </td>
                                </tr>
                                {lines.length > 0 ? (
                                  lines.map((line, lineIdx) => (
                                    <tr key={`${group.providerId}-line-${lineIdx}`}
                                        className={line.type === 'previousBalance' ? 'previous-balance-row' : ''}>
                                      <td>{line.date}</td>
                                      <td className="description-cell">{line.desc}</td>
                                      <td className="debit-cell">{line.debit}</td>
                                      <td className="credit-cell">{line.credit}</td>
                                    </tr>
                                  ))
                                ) : (
                                  <tr>
                                    <td colSpan="4" style={{ textAlign: 'center', fontStyle: 'italic' }}>
                                      No transactions for this provider
                                    </td>
                                  </tr>
                                )}
                                {/* Provider subtotal - Fixed with proper calculation */}
                                {Math.abs(providerSubtotal) > 0.001 && (
                                  <tr className="subtotal-row">
                                    <td colSpan="2" className="subtotal-label"><strong>Subtotal</strong></td>
                                    <td className="subtotal-value"><strong>{formatDebit(providerSubtotal)}</strong></td>
                                    <td></td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })
                      ) : (
                        <tr>

                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Aging Table */}
                <div className="table-wrapper">
                  <table className="statement-table center">
                    <thead>
                      <tr>
                        <th>1-30 Days</th>
                        <th>31-60 Days</th>
                        <th>61-90 Days</th>
                        <th>91+ Days</th>
                        <th>New Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="aging-cell">{(statement.oneTo30Days || 0)}</td>
                        <td className="aging-cell">{(statement.thirtyOneTo60Days || 0)}</td>
                        <td className="aging-cell">{(statement.sixtyOneTo90Days || 0)}</td>
                        <td className="aging-cell">{(statement.ninetyPlusDays || 0)}</td>
                        <td className="aging-cell total"><strong>{(statement.newBalance || 0)}</strong></td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="statement-footer">
                  <div>{statement.accountHolder}</div>
                  <div className="footer-center">{statement.footer || ''}</div>
                  <div>Page {idx + 1} of {statements.filter(s =>
                    !(filters.dontPrintZeroBalance &&
                      Number(s.newBalance || 0) === 0 &&
                      Number(s.previousBalance || 0) === 0)
                  ).length}</div>
                </div>
              </div>
            );
          })}
        </>
      )}

      <style>{`
        .statement-container {
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          font-size: 14px;
          color: #000;
          padding: 20px;
          max-width: 100%;
          margin: auto;
          position: relative;
          box-sizing: border-box;
        }
        .error-message {
          color: red;
          padding: 20px;
          font-weight: bold;
        }
        .header-controls {
          display: flex;
          justify-content: flex-end;
          align-items: flex-start;
          margin-bottom: 20px;
          flex-wrap: wrap;
          gap: 20px;
        }
        .print-button {
          background-color: #004aad;
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 4px;
          cursor: pointer;
          font-weight: bold;
          margin-bottom: 10px;
        }
        .print-button:hover {
          background-color: #003a8c;
        }
        .preview-badge {
          background-color: #ffc107;
          color: #000;
          padding: 8px 16px;
          border-radius: 4px;
          margin-bottom: 20px;
          display: inline-block;
          font-weight: bold;
        }
        .header-section {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          border-bottom: 1px solid #ccc;
          padding-bottom: 10px;
          margin-bottom: 20px;
          width: 100%;
        }
        .lab-info {
          flex: 1;
          text-align: left;
          min-width: 200px;
          white-space: nowrap;
        }
        .header-right {
          display: flex;
          align-items: flex-start;
          gap: 50px;
        }
        .statement-info {
          text-align: left;
          font-size: 13px;
          white-space: nowrap;
        }
        .statement-date {
          font-size: 15px;
        }
        .contact-info {
          font-size: 13px;
        }
        .logo-container-right {
          display: flex;
          justify-content: flex-end;
        }
        .header-logo {
          height: 50px;
          max-width: 120px;
          object-fit: contain;
        }
        .statement-title {
          text-align: right;
          margin-top: 0;
          margin-bottom: 10px;
        }
        .client-payment-section {
          display: flex;
          justify-content: space-between;
          flex-wrap: wrap;
          margin-top: 10px;
          margin-bottom: 20px;
          gap: 20px;
        }
        .client-info {
          font-size: 13px;
        }
        .payment-details {
          max-width: 100%;
          font-size: 13px;
        }
        .table-wrapper {
          overflow-x: auto;
          margin-bottom: 30px;
        }
        .statement-table {
          width: 100%;
          border-collapse: collapse;
        }
        .statement-table th,
        .statement-table td {
          border: 1px solid #000;
          padding: 6px 8px;
        }
        .statement-table th {
          background-color: #004aad;
          color: white;
          font-weight: bold;
          font-size: 13px;
          text-align: center;
        }
        .statement-table.center th {
          text-align: center;
        }
        .statement-table.center td {
          text-align: right;
        }
        
        /* Description column alignment - left aligned */
        .statement-table td.description-cell,
        .statement-table td:nth-child(2) {
          text-align: right !important;
        }
        
        /* Debit column alignment - right aligned */
        .statement-table td.debit-cell,
        .statement-table td:nth-child(3) {
          text-align: right !important;
        }
        
        /* Credit column alignment - right aligned */
        .statement-table td.credit-cell,
        .statement-table td:nth-child(4) {
          text-align: right !important;
        }
        
        /* Aging table cells */
        .aging-cell {
          text-align: right !important;
        }
        
        .aging-cell.total {
          font-weight: bold;
        }
        
        .provider-row {
          font-weight: bold;
        }
        .provider-row td {
          padding: 8px 10px;
        }
        .previous-balance-row td {
          font-weight: normal;
        }
        .previous-balance-row td:nth-child(2) {
          font-weight: bold;
        }
        .sub-row td {
          font-size: 12px;
          border-top: none;
          font-style: normal !important;
          font-weight: normal !important;
        }
        
        /* Subtotal row styling */
        .statement-table .subtotal-row td {
          font-weight: bold;
        }
        
        .statement-table .subtotal-row td.subtotal-label {
          text-align: right;
        }
        
        .statement-table .subtotal-row td.subtotal-value {
          text-align: right;
        }
        
        .statement-table .subtotal-row td:last-child {
          text-align: right;
        }
        
        .statement-footer {
          display: flex;
          justify-content: space-between;
          font-size: 12px;
          color: #444;
          margin-top: 30px;
          padding-top: 10px;
          border-top: 1px solid #ccc;
        }
        .footer-center {
          text-align: center;
          flex-grow: 1;
        }
        @media print {
          body {
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .header-controls, .preview-badge {
            display: none !important;
          }
          .statement-container {
            padding: 0;
          }
          @page {
            size: A4;
            margin: 12mm;
          }
          .statement-wrapper {
            page-break-inside: avoid;
            margin-bottom: 0 !important;
          }
          .statement-table th {
            color: #000 !important;
            background-color: #fff !important;
            border: 1px solid #000 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .provider-row {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          thead {
            display: table-header-group;
          }
          
          /* Keep alignment in print */
          .statement-table td:nth-child(2) {
            text-align: left !important;
          }
          
          .statement-table td:nth-child(3),
          .statement-table td:nth-child(4) {
            text-align: right !important;
          }
        }
      `}</style>
    </div>
  );
};

export default PreviewStatement;